'use client';

import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAction } from 'next-safe-action/hooks';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { forgotPasswordSchema, type ForgotPasswordInput } from '../types/schemas';
import { requestPasswordResetAction } from '../actions/request-password-reset';

export function ForgotPasswordForm() {
  const router = useRouter();

  const form = useForm<ForgotPasswordInput>({
    resolver: zodResolver(forgotPasswordSchema),
    mode: 'onSubmit',
    defaultValues: { email: '' },
  });

  const { execute, status } = useAction(requestPasswordResetAction, {
    onSuccess: () => {
      const email = form.getValues('email');
      router.push(
        `/forgot-password/confirmation?email=${encodeURIComponent(email)}`,
      );
    },
    onError: () => {
      toast.error('Something went wrong. Please try again.');
    },
  });

  const isExecuting = status === 'executing';

  function onSubmit(values: ForgotPasswordInput) {
    execute(values);
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="font-sans text-sm font-medium text-foreground">
                Email address
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="email"
                  placeholder="you@company.com"
                  disabled={isExecuting}
                  className="font-sans"
                />
              </FormControl>
              <FormMessage className="text-destructive text-xs font-sans" />
            </FormItem>
          )}
        />

        <Button
          type="submit"
          disabled={isExecuting}
          className="w-full bg-accent text-accent-foreground hover:bg-accent/90 hover-glow-amber font-sans font-medium transition-all"
        >
          {isExecuting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Sending…
            </>
          ) : (
            'Send reset link'
          )}
        </Button>
      </form>
    </Form>
  );
}
